import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FirebaseContext } from '../Firebase';
import { AuthUserContext } from '../Session';
import * as ROLES from '../../constants/roles';
import './Blog.css';

const SUPPORTED_LANGUAGES = ['en', 'es', 'fr'];

const BlogAdminPage = () => {
  const firebase = useContext(FirebaseContext);
  const authUser = useContext(AuthUserContext);
  const { t } = useTranslation();
  const LANGUAGE_NAMES = {
    en: t('blogAdmin.language.en', 'English'),
    es: t('blogAdmin.language.es', 'Español'),
    fr: t('blogAdmin.language.fr', 'Français')
  };
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    translations: {
      en: { title: '', excerpt: '', content: '', tags: '' },
      es: { title: '', excerpt: '', content: '', tags: '' },
      fr: { title: '', excerpt: '', content: '', tags: '' }
    },
    status: 'draft',
    author: authUser?.username || authUser?.email || '',
    featuredImage: ''
  });

  useEffect(() => {
    loadAllArticles();
  }, []);

  const loadAllArticles = async () => {
    try {
      const data = await firebase.doGetAllArticles();
      setArticles(data);
    } catch (error) {
      console.error('Error loading articles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Convert tags strings to arrays for each language
      const translations = {};
      SUPPORTED_LANGUAGES.forEach(lang => {
        translations[lang] = {
          title: form.translations[lang].title,
          excerpt: form.translations[lang].excerpt,
          content: form.translations[lang].content,
          tags: form.translations[lang].tags.split(',').map(t => t.trim()).filter(Boolean)
        };
      });

      // Collect all unique tags from all languages for root-level querying
      const allTags = new Set();
      SUPPORTED_LANGUAGES.forEach(lang => {
        const langTags = form.translations[lang].tags.split(',').map(t => t.trim()).filter(Boolean);
        langTags.forEach(tag => allTags.add(tag));
      });

      // Generate unique slug (or use existing slug when editing)
      const slug = editing || await firebase.generateUniqueSlug(form.translations.en.title);

      const articleData = {
        translations,
        tags: Array.from(allTags), // Store tags at root level for querying
        slug,
        status: form.status,
        author: form.author,
        featuredImage: form.featuredImage
      };
      
      await firebase.doSaveArticle(articleData);
      alert(t('blogAdmin.article_saved','Article saved!'));
      handleCancel();
      loadAllArticles();
    } catch (error) {
      alert(t('blogAdmin.error_saving_article','Error saving article: ') + error.message);
    }
  };

  const handleEdit = (article) => {
    setEditing(article.slug);
    
    // Populate form with existing translations
    const translations = {};
    SUPPORTED_LANGUAGES.forEach(lang => {
      translations[lang] = {
        title: article.translations?.[lang]?.title || '',
        excerpt: article.translations?.[lang]?.excerpt || '',
        content: article.translations?.[lang]?.content || '',
        tags: article.translations?.[lang]?.tags?.join(', ') || ''
      };
    });

    setForm({
      translations,
      status: article.status,
      author: article.author,
      featuredImage: article.featuredImage || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (slug) => {
      if (window.confirm(t('blogAdmin.delete_confirm','Delete this article? This action cannot be undone.'))) {
      try {
        await firebase.doDeleteArticle(slug);
        alert(t('blogAdmin.article_deleted','Article deleted!'));
        loadAllArticles();
      } catch (error) {
        alert(t('blogAdmin.error_deleting_article','Error deleting article: ') + error.message);
      }
    }
  };

  const handleCancel = () => {
    setEditing(null);
    setForm({
      translations: {
        en: { title: '', excerpt: '', content: '', tags: '' },
        es: { title: '', excerpt: '', content: '', tags: '' },
        fr: { title: '', excerpt: '', content: '', tags: '' }
      },
      status: 'draft',
      author: authUser?.username || authUser?.email || '',
      featuredImage: ''
    });
  };

  // Handle file upload
  const handleFileUpload = (e, lang) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/html') {
      const reader = new FileReader();
      reader.onload = (event) => {
        handleHtmlImport(event.target.result, lang);
      };
      reader.readAsText(file);
    } else {
      alert(t('blogAdmin.invalid_html_file','Please select a valid HTML file'));
    }
  };

  // Parse HTML file and extract content
  const handleHtmlImport = (htmlContent, lang) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      
      // Extract title from h1 or title tag
      const h1 = doc.querySelector('h1');
      const titleTag = doc.querySelector('title');
      const title = h1?.textContent || titleTag?.textContent || '';
      
      // Extract table of contents as excerpt
      const tocDiv = doc.querySelector('.toc');
      let excerpt = '';
      if (tocDiv) {
        excerpt = tocDiv.outerHTML;
      }
      
      // Extract main content (article body or sections)
      const article = doc.querySelector('article');
      const sections = article ? article.innerHTML : doc.body.innerHTML;
      
      // Extract tags from meta keywords
      const metaKeywords = doc.querySelector('meta[name="keywords"]');
      const tags = metaKeywords ? metaKeywords.getAttribute('content') : '';
      
      // Extract featured image from og:image or first img
      const ogImage = doc.querySelector('meta[property="og:image"]');
      const firstImg = doc.querySelector('img');
      const featuredImage = ogImage?.getAttribute('content') || firstImg?.src || '';
      
      // Update form with extracted data for specific language
      setForm(prev => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: {
            title,
            excerpt,
            content: sections,
            tags
          }
        },
        featuredImage: featuredImage || prev.featuredImage
      }));
      
      alert(t('blogAdmin.html_imported','HTML imported successfully for {{language}}!', { language: LANGUAGE_NAMES[lang] }));
    } catch (error) {
      alert('Error parsing HTML: ' + error.message);
    }
  };

  // Check if user is admin
  if (!authUser || !authUser.roles || !authUser.roles[ROLES.ADMIN]) {
    return (
      <div className="blog-admin">
        <h1>{t('blogAdmin.access_denied','Access Denied')}</h1>
        <p>{t('blogAdmin.admin_only','You must be an administrator to access this page.')}</p>
      </div>
    );
  }

  return (
    <div className="blog-admin">
      <h1>{t('blogAdmin.title','Blog Administration')}</h1>
      
      <div className="admin-layout">
        <div className="article-form">
          <h2>{editing ? t('blogAdmin.edit_article','Edit Article') : t('blogAdmin.new_article','New Article')}</h2>
          
          <form onSubmit={handleSubmit}>
            {/* HTML File Uploads for All Languages */}
            <div className="form-section" style={{backgroundColor: '#f0f7ff', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem'}}>
              <h3 style={{marginTop: 0, marginBottom: '1rem', color: '#0066cc'}}>{t('blogAdmin.upload_html_title','Upload HTML Files')}</h3>
              <p style={{fontSize: '0.9em', color: '#666', marginBottom: '1.5rem'}}>
                {t('blogAdmin.upload_html_text','Upload your HTML blog files for each language. The system will automatically extract the title, table of contents (as excerpt), content, and tags.')}
              </p>
              
              {SUPPORTED_LANGUAGES.map(lang => (
                <div key={lang} className="form-group" style={{marginBottom: '1rem'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600}}>
                    <span style={{minWidth: '80px'}}>{LANGUAGE_NAMES[lang]}:</span>
                    <input
                      type="file"
                      accept=".html"
                      onChange={(e) => handleFileUpload(e, lang)}
                      style={{flex: 1}}
                    />
                    {form.translations[lang].title && (
                      <span style={{color: '#28a745', fontSize: '0.9em'}}>✓ Loaded</span>
                    )}
                  </label>
                  {form.translations[lang].title && (
                    <div style={{marginTop: '0.5rem', marginLeft: '90px', fontSize: '0.85em', color: '#666'}}>
                      Title: {form.translations[lang].title.substring(0, 60)}...
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Article Metadata */}
            <div className="form-section">
              <h3 style={{marginBottom: '1rem'}}>{t('blogAdmin.article_details','Article Details')}</h3>
              
              <div className="form-group">
                <label>{t('blogAdmin.featured_image','Featured Image URL')}</label>
                <input
                  type="text"
                  placeholder="https://example.com/image.jpg"
                  value={form.featuredImage}
                  onChange={e => setForm({...form, featuredImage: e.target.value})}
                />
              </div>
              
              <div className="form-group">
                <label>{t('blogAdmin.author','Author')}</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={e => setForm({...form, author: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{t('blogAdmin.status.label','Status')}</label>
                <select
                  value={form.status}
                  onChange={e => setForm({...form, status: e.target.value})}
                >
                  <option value="draft">{t('blogAdmin.status.draft','Draft')}</option>
                  <option value="published">{t('blogAdmin.status.published','Published')}</option>
                </select>
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editing ? t('blogAdmin.update_article','Update Article') : t('blogAdmin.create_article','Create Article')}
              </button>
              
              {editing && (
                <button type="button" onClick={handleCancel} className="btn-secondary">
                  {t('blogAdmin.cancel_edit','Cancel Edit')}
                </button>
              )}
            </div>
          </form>
        </div>
        
        <div className="articles-list">
          <h2>{t('blogAdmin.all_articles','All Articles')} ({articles.length})</h2>
          {articles.length === 0 ? (
            <p className="no-articles-admin">{t('blogAdmin.no_articles','No articles yet. Create your first one!')}</p>
          ) : (
            articles.map(article => {
              const enTitle = article.translations?.en?.title || article.slug;
              return (
                <div key={article.slug} className="article-item">
                  <div className="article-item-content">
                    <h3>{enTitle}</h3>
                    <div className="article-item-meta">
                      <span className={`status-badge ${article.status}`}>
                        {t(`blogAdmin.status.${article.status}`, article.status)}
                      </span>
                      <span>{article.updatedAt?.toDate ? new Date(article.updatedAt.toDate()).toLocaleDateString() : t('blogAdmin.recent','Recent')}</span>
                      {article.views && <span>• {article.views} {t('blog.views','views')}</span>}
                      {/* Show language indicators */}
                      <span>
                        • {SUPPORTED_LANGUAGES.filter(lang => article.translations?.[lang]?.title).map(lang => lang.toUpperCase()).join(', ')}
                      </span>
                    </div>
                  </div>
                  <div className="article-item-actions">
                    <button onClick={() => handleEdit(article)} className="btn-edit">
                      {t('edit')}
                    </button>
                    <button onClick={() => handleDelete(article.slug)} className="btn-delete">
                      {t('delete')}
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogAdminPage;
